import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../lib/auth-context';
import { Button } from '../ui/button';
import { ScrollArea } from '../ui/scroll-area';
import { Avatar, AvatarFallback } from '../ui/avatar';
import { Sheet, SheetContent, SheetTrigger } from '../ui/sheet';
import {
  LayoutDashboard, Users, Wallet, TrendingUp, FileText, Settings, Shield,
  Smartphone, CreditCard, History, Landmark, Newspaper, LogOut,
  Menu, ChevronDown, ChevronRight,
  MessageSquare, MessageCircle, Server, Code2,
  Package, Percent, Layers, Ruler, Ban, ShieldOff,
  Crown, Key, Tv, RadioTower, Bell, Grid3X3, Zap, Image, Globe,
  Search, User, ArrowRightLeft,
} from 'lucide-react';

const DashboardLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState(['main', 'finance']);

  const toggleGroup = (group) => {
    setExpandedGroups(prev =>
      prev.includes(group) ? prev.filter(g => g !== group) : [...prev, group]
    );
  };

  const getNavGroups = () => {
    if (user?.role === 'admin') {
      return [
        {
          id: 'main', label: 'Main',
          items: [
            { path: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
            { path: '/services', icon: Grid3X3, label: 'My Services' },
            { path: '/recharge-management', icon: Smartphone, label: 'Recharge Mgmt' },
          ]
        },
        {
          id: 'finance', label: 'Finance',
          items: [
            { path: '/wallet', icon: Wallet, label: 'Wallet' },
            { path: '/credit-debit', icon: CreditCard, label: 'Credit / Debit' },
            { path: '/commissions', icon: TrendingUp, label: 'Commissions' },
            { path: '/reports', icon: FileText, label: 'Reports' },
          ]
        },
        {
          id: 'users', label: 'Users',
          items: [
            { path: '/users', icon: Users, label: 'User Management' },
            { path: '/super-distributors', icon: Crown, label: 'Super Distributors' },
            { path: '/api-users', icon: Key, label: 'API Users' },
          ]
        },
        {
          id: 'commission', label: 'Commission Setup',
          items: [
            { path: '/commission-packages', icon: Package, label: 'Packages' },
            { path: '/operator-commissions', icon: Percent, label: 'Per Operator' },
            { path: '/commission-ranges', icon: Layers, label: 'By Range' },
          ]
        },
        {
          id: 'controls', label: 'Controls',
          items: [
            { path: '/amount-ranges', icon: Ruler, label: 'Amount Range' },
            { path: '/invalid-amounts', icon: Ban, label: 'Invalid Amounts' },
            { path: '/user-operator-blocks', icon: ShieldOff, label: 'Operator Block' },
          ]
        },
        {
          id: 'integrations', label: 'Integrations',
          items: [
            { path: '/whatsapp-api', icon: MessageSquare, label: 'WhatsApp API' },
            { path: '/whatsapp-settings', icon: MessageCircle, label: 'WhatsApp Tpl.' },
            { path: '/recharge-api', icon: Server, label: 'Recharge API' },
            { path: '/operator-codes', icon: Code2, label: 'Operator Codes' },
            { path: '/bill-operators', icon: Zap, label: 'Bill Operators' },
            { path: '/banner-manager', icon: Image, label: 'Banners' },
            { path: '/api-config', icon: Server, label: 'API Config' },
            { path: '/change-api', icon: ArrowRightLeft, label: 'Change API' },
            { path: '/plan-api-manager', icon: Globe, label: 'Plan API' },
            { path: '/server-info', icon: Server, label: 'System Monitor' },
          ]
        },
        {
          id: 'system', label: 'System',
          items: [
            { path: '/complaints', icon: MessageSquare, label: 'Complaints' },
            { path: '/banks', icon: Landmark, label: 'Bank Manager' },
            { path: '/news', icon: Newspaper, label: 'News Manager' },
            { path: '/admin-settings', icon: Shield, label: 'Admin Settings' },
            { path: '/settings', icon: Settings, label: 'API Settings' },
          ]
        },
      ];
    }

    if (user?.role === 'distributor' || user?.role === 'super_distributor') {
      return [{
        id: 'main', label: 'Main',
        items: [
          { path: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
          { path: '/services', icon: Grid3X3, label: 'My Services' },
          { path: '/wallet', icon: Wallet, label: 'Wallet' },
          { path: '/history', icon: History, label: 'Recharge History' },
          { path: '/users', icon: Users, label: 'My Retailers' },
          { path: '/commissions', icon: TrendingUp, label: 'Commissions' },
          { path: '/commission-chart', icon: Percent, label: 'Commission Chart' },
          { path: '/reports', icon: FileText, label: 'Reports' },
        ]
      }];
    }

    return [{
      id: 'main', label: 'Main',
      items: [
        { path: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
        { path: '/services', icon: Grid3X3, label: 'My Services' },
        { path: '/wallet', icon: Wallet, label: 'Wallet' },
        { path: '/history', icon: History, label: 'Recharge History' },
        { path: '/commissions', icon: TrendingUp, label: 'Commissions' },
        { path: '/commission-chart', icon: Percent, label: 'Commission Chart' },
        { path: '/complaints', icon: MessageSquare, label: 'My Complaints' },
      ]
    }];
  };

  const navGroups = getNavGroups();

  const NavItem = ({ item, mobile = false }) => {
    const isActive = location.pathname === item.path;
    const Icon = item.icon;
    return (
      <Link
        to={item.path}
        onClick={() => mobile && setSidebarOpen(false)}
        className={`group flex items-center gap-3 px-3 py-2.5 rounded-lg text-[13px] font-medium transition-all duration-200 ${
          isActive
            ? 'bg-[#4680ff]/10 text-[#4680ff] font-semibold'
            : 'text-slate-400 hover:text-white hover:bg-white/[0.05]'
        }`}
        data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
      >
        <Icon className={`w-[18px] h-[18px] flex-shrink-0 transition-colors ${isActive ? 'text-[#4680ff]' : 'text-slate-500 group-hover:text-slate-300'}`} strokeWidth={1.8} />
        <span className="truncate">{item.label}</span>
        {isActive && <div className="ml-auto w-1.5 h-1.5 rounded-full bg-[#4680ff]" />}
      </Link>
    );
  };

  const SidebarContent = ({ mobile = false }) => (
    <div className="flex flex-col h-full" style={{ background: '#0f172a' }}>
      {/* Brand */}
      <div className="px-5 pt-5 pb-4">
        <Link to="/dashboard" className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl flex items-center justify-center bg-gradient-to-br from-[#4680ff] to-[#2563eb] shadow-lg shadow-blue-600/30">
            <RadioTower className="w-5 h-5 text-white" strokeWidth={2} />
          </div>
          <div className="leading-none">
            <span className="text-[15px] font-bold text-white tracking-wide block" style={{ fontFamily: 'Outfit, sans-serif' }}>TRIPATHY</span>
            <span className="text-[10px] font-bold text-orange-400 tracking-[0.18em]">TELECOM</span>
          </div>
        </Link>
      </div>

      {/* Wallet Card - Able Pro style */}
      <div className="mx-4 mb-4 p-3.5 rounded-xl bg-gradient-to-r from-[#4680ff]/10 to-[#4680ff]/5 border border-[#4680ff]/10">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-[10px] text-slate-500 uppercase tracking-wider font-semibold">Balance</p>
            <p className="text-lg font-bold text-white font-mono mt-0.5" data-testid="sidebar-balance">
              &#8377;{(user?.wallet_balance || 0).toLocaleString('en-IN')}
            </p>
          </div>
          <div className="w-8 h-8 rounded-lg bg-[#4680ff]/15 flex items-center justify-center">
            <Wallet className="w-4 h-4 text-[#4680ff]" />
          </div>
        </div>
      </div>

      {/* Navigation */}
      <ScrollArea className="flex-1 px-3">
        <nav className="space-y-1 pb-4">
          {navGroups.map((group) => {
            const isExpanded = expandedGroups.includes(group.id);
            const hasActive = group.items.some(i => location.pathname === i.path);
            const isSingle = navGroups.length === 1;

            return (
              <div key={group.id} className="mb-1">
                {!isSingle && (
                  <button
                    onClick={() => toggleGroup(group.id)}
                    className={`w-full flex items-center justify-between px-3 py-2 text-[10px] font-bold uppercase tracking-[0.12em] transition-colors rounded-md ${
                      hasActive ? 'text-[#4680ff]' : 'text-slate-600 hover:text-slate-400'
                    }`}
                    data-testid={`group-${group.id}`}
                  >
                    {group.label}
                    {isExpanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                  </button>
                )}
                {(isSingle || isExpanded) && (
                  <div className="space-y-0.5 mt-0.5">
                    {group.items.map((item) => (
                      <NavItem key={item.path} item={item} mobile={mobile} />
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </nav>
      </ScrollArea>

      {/* Developer by Supro */}
      <div className="px-4 py-3 border-t border-white/[0.06]">
        <p className="text-[10px] text-slate-600 uppercase tracking-[0.2em] text-center font-semibold">
          Developer by Supro
        </p>
      </div>

      {/* User Footer */}
      <div className="p-3 border-t border-white/[0.06]">
        <div className="flex items-center gap-3 px-2 py-2">
          <Avatar className="w-8 h-8 ring-2 ring-[#4680ff]/30">
            <AvatarFallback className="bg-[#4680ff] text-white text-xs font-bold">
              {user?.name?.charAt(0)?.toUpperCase() || 'U'}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-white truncate">{user?.name}</p>
            <p className="text-[10px] text-slate-500 capitalize font-medium">{user?.role?.replace('_', ' ')}</p>
          </div>
          <Button
            variant="ghost" size="icon"
            onClick={() => { logout(); navigate('/login'); }}
            className="text-slate-500 hover:text-red-400 hover:bg-white/[0.05] h-8 w-8"
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#f8fafc]">
      {/* Mobile header */}
      <div className="lg:hidden sticky top-0 z-50 flex items-center justify-between px-4 h-14 bg-white/80 backdrop-blur-xl border-b border-slate-200/80 shadow-sm">
        <Sheet open={sidebarOpen} onOpenChange={setSidebarOpen}>
          <SheetTrigger asChild>
            <Button variant="ghost" size="icon" className="h-9 w-9 text-slate-700" data-testid="mobile-menu-btn">
              <Menu className="w-5 h-5" />
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="p-0 w-[272px] border-0">
            <SidebarContent mobile />
          </SheetContent>
        </Sheet>
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg flex items-center justify-center bg-gradient-to-br from-[#4680ff] to-[#2563eb]">
            <RadioTower className="w-4 h-4 text-white" />
          </div>
          <span className="text-sm font-bold text-slate-800" style={{ fontFamily: 'Outfit, sans-serif' }}>TRIPATHY <span className="text-orange-500">TELECOM</span></span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs font-bold font-mono text-[#4680ff] bg-blue-50 px-2.5 py-1 rounded-lg border border-blue-100" data-testid="mobile-balance">
            &#8377;{(user?.wallet_balance || 0).toLocaleString('en-IN')}
          </span>
          <Link to="/profile" className="w-8 h-8 rounded-full bg-[#4680ff] flex items-center justify-center shadow-sm" data-testid="mobile-profile-btn">
            <span className="text-xs font-bold text-white">{user?.name?.charAt(0)?.toUpperCase() || 'U'}</span>
          </Link>
        </div>
      </div>

      <div className="flex">
        {/* Desktop sidebar */}
        <aside className="hidden lg:block w-[260px] h-screen sticky top-0 flex-shrink-0">
          <SidebarContent />
        </aside>

        {/* Main content area */}
        <div className="flex-1 min-w-0 flex flex-col">
          {/* Desktop Header - Able Pro style */}
          <header className="hidden lg:flex items-center justify-between h-16 px-6 bg-white/80 backdrop-blur-md border-b border-slate-200 sticky top-0 z-40">
            <div className="flex items-center gap-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search..."
                  className="pl-10 pr-4 h-9 w-64 rounded-lg bg-slate-50 border border-slate-200 text-sm text-slate-600 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#4680ff]/20 focus:border-[#4680ff] transition-all"
                  data-testid="header-search"
                />
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="icon" className="h-9 w-9 text-slate-500 hover:text-slate-700 relative" data-testid="header-notifications">
                <Bell className="w-[18px] h-[18px]" />
                <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-red-500 rounded-full" />
              </Button>
              <div className="h-8 w-px bg-slate-200" />
              <Link to="/profile" className="flex items-center gap-2.5 px-2 py-1.5 rounded-lg hover:bg-slate-50 transition-colors" data-testid="header-profile">
                <Avatar className="w-8 h-8 ring-2 ring-[#4680ff]/20">
                  <AvatarFallback className="bg-[#4680ff] text-white text-xs font-bold">
                    {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                  </AvatarFallback>
                </Avatar>
                <div className="hidden xl:block">
                  <p className="text-sm font-medium text-slate-800 leading-none">{user?.name}</p>
                  <p className="text-[10px] text-slate-400 capitalize mt-0.5">{user?.role?.replace('_', ' ')}</p>
                </div>
              </Link>
            </div>
          </header>

          {/* Page Content */}
          <main className="flex-1">
            <div className="p-4 md:p-6 lg:p-8 max-w-[1400px]">
              {children}
            </div>
          </main>

          {/* Footer - Developer by Supro */}
          <footer className="py-4 px-6 border-t border-slate-100 bg-white/50">
            <p className="text-center text-sm text-slate-400 font-medium" data-testid="footer-branding">
              Developer by <span className="font-semibold text-slate-500">Supro</span>
            </p>
          </footer>
        </div>
      </div>

      {/* WhatsApp Floating Button */}
      <a
        href="https://wa.me/919800000000"
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full bg-[#25D366] flex items-center justify-center shadow-lg shadow-green-500/30 hover:scale-110 transition-transform duration-200"
        data-testid="whatsapp-fab"
        title="Chat on WhatsApp"
      >
        <svg viewBox="0 0 24 24" className="w-7 h-7 fill-white">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
        </svg>
      </a>
    </div>
  );
};

export default DashboardLayout;
